import { useState, useCallback, useRef, useMemo } from "react";
import { Loader2, ChevronDown, GitCompareArrows } from "lucide-react";
import { tileCardStyle, tileHoverHandlers } from "../utils";
import ReportCitationButton from "../../Reports/ReportCitationButton";
import ArticleDetailPanel from "../../../Universal/ArticleCardDetailPanel";
import CitationLoadingAnimation from "../../../Universal/CitationLoadingAnimation";
import { useRightSidebar } from "../../../contexts/RightSidebarContext";
import { getTheme } from "../../../Universal/useNavTheme";
import { getDocIdData } from "../../../../api/universal";
import { scrapePublicCitation } from "../../../../api/chat";

/* ── Timestamp helpers ── */

const formatTimestamp = (ts) => {
  if (!ts) return "";
  try {
    const num = typeof ts === "string" ? Number(ts) : ts;
    const date = new Date(
      typeof num === "number" && num < 1e12 ? num * 1000 : num,
    );
    if (isNaN(date.getTime())) return ts;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return ts;
  }
};

/* ── Word-level diff ── */

const computeWordDiff = (oldText, newText) => {
  const oldWords = (oldText || "").split(/(\s+)/);
  const newWords = (newText || "").split(/(\s+)/);

  const m = oldWords.length;
  const n = newWords.length;

  // LCS table
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (oldWords[i - 1] === newWords[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  // Backtrack to build diff
  const result = [];
  let i = m;
  let j = n;
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldWords[i - 1] === newWords[j - 1]) {
      result.unshift({ type: "same", text: oldWords[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      result.unshift({ type: "added", text: newWords[j - 1] });
      j--;
    } else {
      result.unshift({ type: "removed", text: oldWords[i - 1] });
      i--;
    }
  }

  return result;
};

/* ── Citation helpers ── */

const buildCitations = (sourcesList) => {
  if (!Array.isArray(sourcesList) || sourcesList.length === 0) return [];
  return sourcesList.map((source, i) => {
    if (source.type === "document_id") {
      return {
        index: i + 1,
        doc_id: source.value,
        url: "",
        title: source.title || "",
        like: !!source.like,
        dislike: !!source.dislike,
        catalog: !!source.catalog,
      };
    }
    return {
      index: i + 1,
      url: source.value,
      doc_id: "",
      title: "",
    };
  });
};

const renderContentWithCitations = (
  content,
  citations,
  onSourceClick,
  hoveredRef,
  onHoverIn,
  onHoverOut,
  activeSourceId,
  citationInteractions,
) => {
  if (!content) return null;
  if (!citations || citations.length === 0) return content;

  const parts = [];
  const regex = /\[(\d+)\]/g;
  let match;
  let lastIndex = 0;

  while ((match = regex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push(content.slice(lastIndex, match.index));
    }
    const idx = parseInt(match[1], 10);
    const citation = citations.find((c) => c.index === idx);
    if (citation) {
      parts.push(
        <ReportCitationButton
          key={`cite-${match.index}-${idx}`}
          index={idx}
          citation={citation}
          hoveredRef={hoveredRef}
          onHoverIn={onHoverIn}
          onHoverOut={onHoverOut}
          onSourceClick={() => onSourceClick(citation)}
          activeSourceId={activeSourceId}
          citationInteractions={citationInteractions}
        />,
      );
    } else {
      parts.push(match[0]);
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  return parts;
};

/* Strip citation markers for clean diffing */
const stripCitations = (text) => {
  if (!text) return "";
  return text.replace(/\[\d+\]/g, "");
};

/* ── Diff renderer (side-by-side) ── */

const DiffView = ({ diffParts, t }) => {
  const oldParts = [];
  const newParts = [];

  diffParts.forEach((part, i) => {
    if (part.type === "same") {
      oldParts.push(<span key={i}>{part.text}</span>);
      newParts.push(<span key={i}>{part.text}</span>);
    } else if (part.type === "removed") {
      oldParts.push(
        <span
          key={i}
          className="bg-red-100 text-red-800 line-through dark:bg-red-900/30 dark:text-red-300"
        >
          {part.text}
        </span>,
      );
    } else if (part.type === "added") {
      newParts.push(
        <span
          key={i}
          className="bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300"
        >
          {part.text}
        </span>,
      );
    }
  });

  return (
    <div className="grid grid-cols-2 gap-3">
      <div
        style={{
          borderRight: `1px solid ${t.divider}`,
          color: t.textSecondary,
        }}
        className="pr-3"
      >
        <span
          style={{ color: t.textMuted }}
          className="text-[10px] uppercase tracking-wider font-medium block mb-2"
        >
          Previous
        </span>
        <p
          className="text-sm leading-relaxed whitespace-pre-wrap"
          style={{ margin: 0 }}
        >
          {oldParts}
        </p>
      </div>
      <div style={{ color: t.textSecondary }} className="pl-1">
        <span
          style={{ color: t.textMuted }}
          className="text-[10px] uppercase tracking-wider font-medium block mb-2"
        >
          Current
        </span>
        <p
          className="text-sm leading-relaxed whitespace-pre-wrap"
          style={{ margin: 0 }}
        >
          {newParts}
        </p>
      </div>
    </div>
  );
};

/* ── Main component ── */

const TileReport = ({ data = {}, t }) => {
  const { setRightComponent, setIsRightOpen, setRightSidebarMode } =
    useRightSidebar();

  const [citationInteractions, setCitationInteractions] = useState(() => {
    const initial = {};
    const sources = data.sources_list;
    if (Array.isArray(sources)) {
      sources.forEach((source) => {
        if (source.type === "document_id" && source.value) {
          if (source.like || source.dislike || source.catalog) {
            initial[source.value] = {
              like: !!source.like,
              dislike: !!source.dislike,
              catalog: !!source.catalog,
            };
          }
        }
      });
    }
    return initial;
  });
  const [activeSourceId, setActiveSourceId] = useState(null);
  const hoveredRef = useRef(null);

  const versions = data.versions || [data];
  const [selectedVersionIdx, setSelectedVersionIdx] = useState(0);
  const [showChanges, setShowChanges] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const selectedVersion = versions[selectedVersionIdx] || versions[0];
  const previousVersion = versions[selectedVersionIdx + 1] || null;
  const hasPreviousVersion = !!previousVersion;

  const diffParts = useMemo(() => {
    if (!showChanges || !previousVersion) return null;
    return computeWordDiff(
      stripCitations(previousVersion.content || ""),
      stripCitations(selectedVersion.content || ""),
    );
  }, [showChanges, selectedVersion, previousVersion]);

  const onHoverIn = useCallback((idx) => {
    hoveredRef.current = idx;
  }, []);

  const onHoverOut = useCallback(() => {
    hoveredRef.current = null;
  }, []);

  const openArticlePanel = useCallback(
    (citationKey, article) => {
      const dark = document.documentElement.classList.contains("dark");
      const onInteract = (_cardId, _docId, state) => {
        setCitationInteractions((prev) => ({
          ...prev,
          [citationKey]: state,
        }));
      };
      setRightComponent(
        <ArticleDetailPanel
          key={citationKey}
          article={article}
          cardId={citationKey}
          t={getTheme(dark)}
          onInteract={onInteract}
        />,
      );
    },
    [setRightComponent],
  );

  const handleSourceClick = useCallback(
    (citation) => {
      const citationKey = citation.doc_id || citation.url;
      setActiveSourceId(citationKey);
      setRightSidebarMode(null);
      setIsRightOpen(true);

      if (citation.doc_id) {
        setRightComponent(
          <div className="p-4 text-[13px] text-zinc-500 dark:text-zinc-400 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading source…
          </div>,
        );

        getDocIdData(citation.doc_id)
          .then((response) => {
            const article = response?.data;
            if (!article) {
              setRightComponent(
                <div className="p-6 text-center text-[13px] text-mutedText">
                  Unable to load source
                </div>,
              );
              return;
            }
            openArticlePanel(citationKey, {
              ...article,
              like: typeof article.like === "boolean" ? article.like : false,
              dislike:
                typeof article.dislike === "boolean" ? article.dislike : false,
              catalog:
                typeof article.catalog === "boolean" ? article.catalog : false,
            });
          })
          .catch(() => {
            setRightComponent(
              <div className="p-6 text-center text-[13px] text-mutedText">
                Unable to load source
              </div>,
            );
          });
      } else if (citation.url) {
        setRightComponent(
          <div className="h-full">
            <CitationLoadingAnimation />
          </div>,
        );

        scrapePublicCitation({ url: citation.url })
          .then((res) => {
            const data = res?.data;
            if (!data?.success && !data?.already_saved) {
              setRightComponent(
                <div className="p-6 text-center text-[13px] text-mutedText">
                  Unable to load citation
                </div>,
              );
              return;
            }

            const article = {
              document_id: data.doc_id,
              title: data.title || data.domain || "Untitled",
              url: data.url || citation.url,
              source: data.source || data.domain || "",
              content: data.content || "",
              description: data.description || "",
              screenshot: data.screenshot || "",
              screenshot_full: data.screenshot_full || "",
              target_type: data.target_type || "web_citation",
              timestamp: data.date_published
                ? new Date(data.date_published).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })
                : "",
              like: !!data.like,
              dislike: !!data.dislike,
              catalog: !!data.catalog,
            };

            if (data.like || data.dislike || data.catalog) {
              setCitationInteractions((prev) => ({
                ...prev,
                [citationKey]: {
                  like: !!data.like,
                  dislike: !!data.dislike,
                  catalog: !!data.catalog,
                },
              }));
            }

            setRightComponent(
              <CitationLoadingAnimation
                isComplete={true}
                statusMessage="Source captured"
                onCompleteFinished={() => {
                  openArticlePanel(citationKey, article);
                }}
              />,
            );
          })
          .catch(() => {
            setRightComponent(
              <div className="p-6 text-center text-[13px] text-mutedText">
                Unable to load citation
              </div>,
            );
          });
      }
    },
    [setRightComponent, setIsRightOpen, setRightSidebarMode, openArticlePanel],
  );

  const targetType =
    typeof data.target_type === "string" ? data.target_type.trim() : "";
  const content =
    typeof selectedVersion.content === "string"
      ? selectedVersion.content.trim()
      : "";
  if (!targetType && !content) return null;

  const citations = buildCitations(selectedVersion.sources_list);
  const timestamp = formatTimestamp(selectedVersion.timestamp);

  return (
    <div style={tileCardStyle(t)} {...tileHoverHandlers(t)}>
      <div className="flex flex-col gap-3 p-5">
        {/* Header row: target type badge + controls + timestamp */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span
              style={{
                background: t.badgeBg,
                border: `1px solid ${t.badgeBorder}`,
                color: t.badgeText,
              }}
              className="inline-block rounded-md px-2.5 py-1 text-xs font-medium"
            >
              {targetType}
            </span>

            {/* Version dropdown */}
            {versions.length > 1 && (
              <div className="relative">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setDropdownOpen((v) => !v);
                  }}
                  style={{
                    background: t.labelBg,
                    border: `1px solid ${t.labelBorder}`,
                    color: t.labelText,
                  }}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium cursor-pointer"
                >
                  {timestamp}
                  <ChevronDown className="h-3 w-3" />
                </button>
                {dropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDropdownOpen(false);
                      }}
                    />
                    <div
                      style={{
                        background: t.pageBg,
                        border: `1px solid ${t.cardBorder}`,
                      }}
                      className="absolute left-0 top-full mt-1 z-20 rounded-md shadow-lg min-w-[140px] py-1"
                    >
                      {versions.map((v, idx) => (
                        <button
                          key={idx}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedVersionIdx(idx);
                            setDropdownOpen(false);
                          }}
                          style={{
                            color:
                              idx === selectedVersionIdx
                                ? t.badgeText
                                : t.textSecondary,
                            background:
                              idx === selectedVersionIdx
                                ? t.badgeBg
                                : "transparent",
                          }}
                          className="w-full text-left px-3 py-1.5 text-[11px] hover:opacity-80 transition-opacity"
                        >
                          {formatTimestamp(v.timestamp)}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Show changes toggle */}
            {hasPreviousVersion && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowChanges((v) => !v);
                }}
                style={{
                  background: showChanges ? "rgba(14,165,233,0.1)" : t.labelBg,
                  border: `1px solid ${showChanges ? "rgba(14,165,233,0.4)" : t.labelBorder}`,
                  color: showChanges ? "#0ea5e9" : t.labelText,
                }}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium cursor-pointer transition-all"
              >
                <GitCompareArrows className="h-3 w-3" />
                Changes
              </button>
            )}
          </div>

          {/* Timestamp (shown when no version dropdown) */}
          {versions.length <= 1 && timestamp && (
            <span style={{ color: t.textMuted }} className="text-xs">
              {timestamp}
            </span>
          )}
        </div>

        {/* Divider */}
        <div style={{ borderTop: `1px solid ${t.divider}` }} />

        {/* Content — normal or diff view */}
        {content &&
          (showChanges && diffParts ? (
            <DiffView diffParts={diffParts} t={t} />
          ) : (
            <p
              style={{ color: t.textSecondary, margin: 0 }}
              className="text-sm leading-relaxed whitespace-pre-wrap"
            >
              {renderContentWithCitations(
                content,
                citations,
                handleSourceClick,
                hoveredRef,
                onHoverIn,
                onHoverOut,
                activeSourceId,
                citationInteractions,
              )}
            </p>
          ))}

        {/* Sources summary */}
        {citations.length > 0 && (
          <div className="flex items-center gap-1.5 pt-1">
            <span style={{ color: t.textMuted }} className="text-xs">
              {citations.length} source{citations.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TileReport;
